import { Box, Flex, VStack } from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { getLoginPathForLocation } from '@resistapp/client/utils/login-return-path';
import { OrganisationType } from '@resistapp/common/types';
import {
  canAccessRestrictedAdminViews,
  getAdminIndexPath,
  isResistomapAdminUser,
  shouldForceLabLanding,
} from '@resistapp/client/utils/access';
import type { CSSProperties } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { isResistomapAdminOnlyPath, isRestrictedAdminPath } from './admin-route-access';

const LAB_WORKBOOKS_PATH = '/admin/lab-workbooks';
const SHIPPING_PATH = '/admin/shipping';
const USER_SHIPPING_PATH = '/admin/sample-shipping';
type AdminNavItem = {
  label: string;
  to: string;
  requiresRestrictedAdminAccess?: boolean;
  requiresShipmentModifyAccess?: boolean;
  requiresUserShippingAccess?: boolean;
  requiresResistomapAdminAccess?: boolean;
};

const createLinkStyle =
  (isDisabled: boolean) =>
  ({ isActive }: { isActive: boolean }): CSSProperties => ({
    display: 'block',
    padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
    borderRadius: 6,
    textDecoration: 'none',
    color: isDisabled ? theme.colors.neutral500 : isActive ? theme.colors.blue900 : theme.colors.neutral900,
    backgroundColor: isActive ? theme.colors.blue100 : 'transparent',
    opacity: isDisabled ? 0.6 : 1,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: isDisabled ? 'none' : 'auto',
  });

const navItems: ReadonlyArray<AdminNavItem> = [
  { label: 'Organisations', to: '/admin/organisations', requiresRestrictedAdminAccess: true },
  { label: 'Users', to: '/admin/users', requiresRestrictedAdminAccess: true },
  { label: 'Projects', to: '/admin/projects', requiresRestrictedAdminAccess: true },
  { label: 'Sampling Points', to: '/admin/sampling-points', requiresRestrictedAdminAccess: true },
  { label: 'Samples', to: '/admin/samples', requiresRestrictedAdminAccess: true },
  { label: 'Chips', to: '/admin/chips', requiresRestrictedAdminAccess: true },
  { label: 'Sensors', to: '/admin/sensors', requiresResistomapAdminAccess: true },
  { label: 'Lab Workbooks', to: LAB_WORKBOOKS_PATH },
  { label: 'Sample shipping', to: USER_SHIPPING_PATH, requiresUserShippingAccess: true },
  { label: 'Shipping', to: SHIPPING_PATH, requiresShipmentModifyAccess: true },
];

export function AdminLayout() {
  // Ensure the left nav's right border spans the full viewport height.
  // We subtract the top & bottom page padding so the border aligns visually.
  const navMinHeight = `calc(100vh - ${theme.spacing[6]} - ${theme.spacing[6]})`;
  const location = useLocation();
  const { user, userLoading } = useUser();
  const restrictNavToLab = shouldForceLabLanding(user);

  if (userLoading || user === undefined) {
    return null;
  }

  if (!user) {
    return <Navigate to={getLoginPathForLocation(location)} replace />;
  }

  const hasResistomapAdminAccess = isResistomapAdminUser(user);
  const hasRestrictedAdminAccess = canAccessRestrictedAdminViews(user);
  const hasShipmentModifyAccess = hasResistomapAdminAccess;
  const hasUserShippingAccess =
    !hasShipmentModifyAccess &&
    user.organisations.some(
      organisation =>
        organisation.type === OrganisationType.CUSTOMER || organisation.type === OrganisationType.PROVIDER,
    );
  const restrictedPathRequested = isRestrictedAdminPath(location.pathname);
  const resistomapAdminPathRequested = isResistomapAdminOnlyPath(location.pathname);
  const shouldRedirectForRestrictedAccess = !hasRestrictedAdminAccess && restrictedPathRequested;
  const shippingPathRequested =
    location.pathname === SHIPPING_PATH || location.pathname.startsWith(`${SHIPPING_PATH}/`);
  if (!hasShipmentModifyAccess && shippingPathRequested) {
    return <Navigate to={location.pathname.replace(SHIPPING_PATH, USER_SHIPPING_PATH)} replace />;
  }
  const shouldRedirectForResistomapAdminAccess = !hasResistomapAdminAccess && resistomapAdminPathRequested;
  if (shouldRedirectForRestrictedAccess || shouldRedirectForResistomapAdminAccess) {
    return <Navigate to={getAdminIndexPath(user)} replace />;
  }
  const enabledRestrictedPaths = new Set<string>([LAB_WORKBOOKS_PATH]);
  const visibleNavItems = navItems.filter(
    item =>
      (!item.requiresRestrictedAdminAccess || hasRestrictedAdminAccess) &&
      (!item.requiresResistomapAdminAccess || hasResistomapAdminAccess) &&
      (!item.requiresShipmentModifyAccess || hasShipmentModifyAccess) &&
      (!item.requiresUserShippingAccess || hasUserShippingAccess),
  );

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      gap={{ base: theme.spacing[4], lg: theme.spacing[6] }}
      alignItems="flex-start"
      padding={{ base: theme.spacing[3], lg: theme.spacing[6] }}
    >
      <Box
        as="nav"
        width={{ base: '100%', lg: '260px' }}
        flexShrink={0}
        borderRight={{ base: 0, lg: `1px solid ${theme.colors.neutral300}` }}
        borderBottom={{ base: `1px solid ${theme.colors.neutral300}`, lg: 0 }}
        paddingRight={{ base: 0, lg: theme.spacing[4] }}
        paddingBottom={{ base: theme.spacing[3], lg: 0 }}
        position={{ base: 'static', lg: 'sticky' }}
        top={{ lg: theme.spacing[6] }}
        minHeight={{ base: 'auto', lg: navMinHeight }}
      >
        <VStack align="stretch" gap={theme.spacing[1]}>
          {visibleNavItems.map(item => {
            const isDisabled = restrictNavToLab && !enabledRestrictedPaths.has(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={createLinkStyle(isDisabled)}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : undefined}
              >
                {item.label}
              </NavLink>
            );
          })}
        </VStack>
      </Box>
      <div style={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
        <Outlet />
      </div>
    </Flex>
  );
}
